import { useQuery } from "@tanstack/react-query";
import { useAdminPro } from "api/queries/admin";
import { useProProfile } from "api/queries/pros";
import { useIsAdmin } from "domains/auth";
import {
  PROfileAbout,
  PROfileAboutMinimal,
  PROfileAboutSkeleton,
  PROfilePreferences,
  type PreferenceTypeId,
  type ProPreferencesByType,
} from "domains/pros/components/PROfileAbout";
import { PROfileAvailability } from "domains/pros/components/PROfileAvailability";
import React from "react";
import { availabilityQuery, timeBlocksQuery } from "./queries";

type PROfileAboutContainerProps = {
  id: number;
  variant?: "default" | "minimal";
};

export const PROfileAboutContainer: React.FC<PROfileAboutContainerProps> = ({ id, variant = "full" }) => {
  const isAdmin = useIsAdmin();
  const input = { id, isAdmin };
  const { data: proAdminData } = useAdminPro({ id });
  const { data: proProfile } = useProProfile(id);
  // Admins only: `?pro=` is honoured for gigpro admins and a non-admin reads their OWN week, which
  // on Find a Pro / candidate sheets / connect would show the viewer's schedule on another profile.
  const { data: availabilityData } = useQuery(availabilityQuery(input));
  const { data: timeBlocks } = useQuery(timeBlocksQuery(input));
  // A non-list response would throw in render and take the bio and preferences down with it.
  const availability = Array.isArray(availabilityData) ? availabilityData : [];

  // Profile reads only, so a slow availability request cannot hide what already loaded.
  if (!proProfile || !proAdminData) return <PROfileAboutSkeleton />;
  const preferences = proProfile.pro_preferences;
  // `days_of_week` is bare strings, not `{name, description, id}`, so it maps separately below.
  const namedOptions = (options: Array<{ name?: string | null; description?: string | null; id?: number | null }>) =>
    options.map(({ name, description, id }) => ({ label: name ?? description ?? "", id }));

  const preferencesByTypes: Array<ProPreferencesByType> = [
    {
      type: { id: "work-type" as PreferenceTypeId, label: "Work type" },
      preferences: namedOptions(preferences?.pro_contractor_type ?? []),
    },
    {
      type: { id: "positions" as PreferenceTypeId, label: "Positions" },
      preferences: namedOptions(preferences?.skills ?? []),
    },
    {
      type: { id: "days" as PreferenceTypeId, label: "Days" },
      preferences: (preferences?.days_of_week ?? []).map((day) => ({
        label: `${day.charAt(0)}${day.slice(1).toLowerCase()}`,
        id: null,
      })),
    },
    {
      type: { id: "shifts" as PreferenceTypeId, label: "Shifts" },
      preferences: namedOptions(preferences?.time_blocks ?? []),
    },
    {
      type: { id: "hours" as PreferenceTypeId, label: "Hours" },
      preferences: namedOptions(preferences?.hours_per_week ?? []),
    },
  ].filter(({ preferences: values }) => values.length > 0);
  const transformedData = {
    id: proProfile.id,
    bio: proProfile.bio ?? "",
    preferencesByTypes,
    gigproCertifications: proAdminData.pro_certifications
      ?.filter((certification) => certification.status === "CERTIFIED")
      .map(({ certificate }) => certificate),
  };

  if (variant === "minimal") {
    return (
      <>
        <PROfileAboutMinimal bio={transformedData.bio} gigproCertifications={transformedData.gigproCertifications} />
        <h3 className="my-2 text-normal font-semibold">Preferences</h3>
        <PROfilePreferences preferencesByTypes={transformedData.preferencesByTypes} />
        <PROfileAvailability slices={availability} timeBlocks={timeBlocks ?? []} />
      </>
    );
  }

  return <PROfileAbout data={{ ...transformedData, availability, timeBlocks: timeBlocks ?? [] }} />;
};
